<script setup lang="ts">
import Button from '@components/buttons/button.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import ButtonSquare from '@components/buttons/button-square.vue';
import Headline from '@components/headline.vue';
import IntInput from '@components/form/int-input.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import InputLabel from '@src/application/input-label.vue';
import Text from '@components/text.vue';
import { ClusterMinimal } from '../../cluster/interfaces/cluster-minimal.ts';
import { ref, watch } from 'vue';
import { PicklistLine } from '@src/pick/cluster-picklist/picklist-line.ts';
import Tag from '@components/tag.vue';
import Expandable from '@components/expandable.vue';
import { PickCluster } from '@src/pick/cluster-picklist/pick-cluster.ts';
import ItemLoader from '@components/loader/item-loader.vue';
import ItemInfoTrigger from '@components/item-info-trigger.vue';
import { Picked } from '@src/pick/cluster-picklist/picked.ts';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import { Bin } from '@src/warehouse/interfaces/bin.ts';

const { loadPicklistLines, pickCluster, showOpened = false, onPick, onNotFound } = defineProps<{
    pickCluster: PickCluster,
    loadPicklistLines: (cluster: ClusterMinimal|null) => Promise<Array<PicklistLine>>,
    showOpened?: boolean
    onPick: (picked: Picked) => Promise<void>|void
    onNotFound: (picklistLine: PicklistLine) => Promise<void>|void
}>();

const picklistLines = ref<Array<PicklistLine>|null>(null);

const picklistLinesLoading = ref(false);

const isOpen = ref<boolean>(showOpened);

const emit = defineEmits<((event: 'toggle', toggle: boolean) => void)>();

watch(isOpen, () => {
    emit('toggle', isOpen.value);
    loadContent();
}, {
    immediate: true,
});

async function loadContent() {
    if (!isOpen.value) {
        return;
    }

    picklistLinesLoading.value = true;
    picklistLines.value = await loadPicklistLines(pickCluster.cluster);
    picklistLinesLoading.value = false;
}

async function pick(picklistLine: PicklistLine, quantity: number, serial?: string, bin?: Bin) {
    await onPick({
        product: picklistLine.product,
        quantity,
        serial,
        bin,
    });
    await loadContent();
}

async function notFound(picklistLine: PicklistLine) {
    await onNotFound(picklistLine);
}

defineExpose({ loadContent });
</script>

<template>
    <Expandable :show-opened="showOpened" context="light" @show-content="isOpen = $event.showContent">
        <template v-slot:title>
            <HorizontalAlignChildrenWrapper gap="2" center-children :wrap-on-small-screen="false">
                <Tag tag-color="blue" :tag-value="pickCluster.quantity.toString()"/>
                {{ pickCluster.cluster?.name ?? 'Ohne Cluster' }}
            </HorizontalAlignChildrenWrapper>
        </template>
        <CenterChildrenWrapper v-if="picklistLinesLoading">
            <ItemLoader static-loader/>
        </CenterChildrenWrapper>
        <VerticalAlignChildrenWrapper v-else-if="picklistLines !== null" gap-bottom="3" separator avoid-outer-gap>
            <VerticalAlignChildrenWrapper v-for="line in picklistLines" :key="line.bin.idBin + line.product.idProduct" gap-bottom="1" avoid-outer-gap>
                <HorizontalAlignChildrenWrapper :wrap-on-small-screen="false" gap="2" center-children>
                    <Headline type="h3" width="spread">{{ line.bin.binCode }}</Headline>
                    <ItemInfoTrigger :product="line.product" tooltip-position="left"/>
                </HorizontalAlignChildrenWrapper>
                <Text gap="1">{{ line.product.name }}</Text>
                <div v-if="!line.product.serialNumberRequired">
                    <Text bold gap="1">{{ line.product.firstEan }}</Text>
                    <InputLabel label="Anzahl"/>
                    <HorizontalAlignChildrenWrapper :wrap-on-small-screen="false" gap="2" grow-children>
                        <ButtonSquare icon="ban" context="danger" @click="notFound(line)"/>
                        <IntInput :model-value="line.quantity" submit-button="Los" @submit="pick(line, $event, undefined, line.bin)"/>
                    </HorizontalAlignChildrenWrapper>
                </div>
                <div v-else :class="$style.serials_wrapper">
                    <HorizontalAlignChildrenWrapper gap="2" :wrap-on-small-screen="false" :class="$style.serials" grow-children>
                        <ButtonSquare icon="ban" context="danger" @click="notFound(line)"/>
                        <Button v-for="serial in line.serials" :class="$style.serial" @click="pick(line, 1, serial, line.bin)">
                            <span>{{ serial.substring(0, 6) }}</span>
                            <Headline type="h2">{{ serial.substring(6, 9) }}</Headline>
                        </Button>
                    </HorizontalAlignChildrenWrapper>
                </div>
            </VerticalAlignChildrenWrapper>
        </VerticalAlignChildrenWrapper>
    </Expandable>
</template>

<style module lang="scss">
.product {
    cursor: pointer;
}
.serials_wrapper {
    position: relative;

    &::after {
        position: absolute;
        top: 0;
        right: 0;
        width: 2em;
        height: 100%;
        content: '';
        background: linear-gradient(90deg, rgba(255,255,255,0%) 0%, rgba(255,255,255,100%) 100%);
    }
    .serials {
        overflow: auto;
    }
    .serial:last-child {
        margin-right: 2em;
    }
}
</style>
